import React, { useState, useEffect } from 'react';
import { profileAPI, traitsAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PlayerCard } from './PlayerStatsCard';
import { FifaCard } from './FifaCard';
import { MapPin, Phone, Mail, Calendar, Dumbbell } from 'lucide-react';
import { DAYS_OF_WEEK, TIME_SLOTS } from '@/lib/utils';

export const PlayerProfileDialog = ({ userId, open, onOpenChange, sportId = 'sport_calcetto' }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    if (!open) return;
    traitsAPI.getAll(sportId).then((res) => setCatalog(res.data.traits || [])).catch(() => {});
  }, [open, sportId]);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    profileAPI.getPublicProfile(userId)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, open]);

  const user = data?.user;
  const profile = data?.profile;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Profilo Giocatore</DialogTitle>
          <DialogDescription>Informazioni pubbliche del giocatore</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
            <Skeleton className="h-20" />
          </div>
        ) : !user ? (
          <p className="text-center text-muted-foreground py-8">Profilo non disponibile</p>
        ) : (
          <div className="space-y-5 py-2">
            <div className="mx-auto w-full max-w-[260px]">
              <FifaCard player={user} attributes={profile?.sport_profiles?.[sportId] || {}} catalog={catalog} />
            </div>

            <PlayerCard
              userId={userId}
              sportId={sportId}
              profile={profile?.sport_profiles?.[sportId] || profile}
            />

            {/* Info */}
            <div className="space-y-3">
              {profile?.city && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{profile.city}</span>
                </div>
              )}
              {user.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${user.email}`} className="text-primary hover:underline">{user.email}</a>
                </div>
              )}
              {profile?.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${profile.phone}`} className="text-primary hover:underline">{profile.phone}</a>
                </div>
              )}
            </div>

            {/* Bio */}
            {profile?.bio && (
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm whitespace-pre-line">{profile.bio}</p>
              </div>
            )}

            {/* Sports */}
            {profile?.sport_names?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Dumbbell className="h-4 w-4" />
                  Sport praticati
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {profile.sport_names.map(name => (
                    <Badge key={name} variant="outline">{name}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Availability */}
            {profile?.availability?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  Disponibilità
                </h4>
                <div className="space-y-1.5">
                  {profile.availability.map(a => {
                    const dayLabel = DAYS_OF_WEEK.find(d => d.value === a.day)?.label || a.day;
                    const slotLabels = (a.slots || []).map(s => TIME_SLOTS.find(t => t.value === s)?.label || s).join(', ');
                    return (
                      <div key={a.day} className="flex items-center gap-2 text-sm">
                        <span className="font-medium w-24">{dayLabel}</span>
                        <span className="text-muted-foreground">{slotLabels}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PlayerProfileDialog;
